/**
 * Deciding what to persist. Kept pure so it can be tested without a React tree or a context.
 *
 * There are two commit paths and they stay separate functions rather than one with a mode switch:
 * they call different APIs, produce different dispatches and fail differently.
 *
 *   ONE_SHIPMENT   - the plan becomes item.Consignment and is patched onto an existing item.
 *   MANY_SHIPMENTS - the plan becomes several SmartSendItems created through createItemsHelper. That
 *                    path is already pure (GenerateSplitSummary and friends) and is untouched here.
 */
import { ConsignmentApiModel, SmartSendItemApiModel } from '~/lib/api/smartsend/types';

/**
 * Which just-created item does a pending consignment belong to?
 *
 * The create-group endpoint's request payload has no Consignment field, so a merged item built with a
 * multi-parcel plan has to be saved a second time once it has a real Id. This resolves the target of
 * that follow-up save. It stays a separate round trip until the API can carry the consignment on
 * create; what changed is that the decision is now testable rather than buried in an async callback.
 *
 * 'AMBIGUOUS' means the consignment cannot be safely attached - the caller should surface an error
 * rather than guess, because attaching a consignment to the wrong shipment mis-declares it.
 */
export function resolveConsignmentTarget(
  requested: SmartSendItemApiModel[],
  added: SmartSendItemApiModel[]
): { itemId: string; consignment: ConsignmentApiModel } | 'NONE' | 'AMBIGUOUS' {
  const pending = requested.find((i) => i.Id === null && i.Consignment);

  if (!pending) {
    return 'NONE';
  }

  const grouped = (added ?? []).filter((i) => i.Source?.Id === 'grouped');

  if (grouped.length !== 1) {
    return 'AMBIGUOUS';
  }

  return { itemId: grouped[0].Id, consignment: pending.Consignment };
}
